/**
 * SOLOMON — DEAL STAGE STEPPER
 * Single pipeline state control. Replaces the old "Change state" dropdown.
 * Renders the deal ladder as a click-to-advance track; each click hits the
 * existing PATCH /api/leads/:id/state with triggeredBy:'operator' (which
 * bypasses the strict transition graph server-side) and an auto-generated
 * reason, then asks the parent to refresh in place.
 *
 * Cold-call outcomes (contacted / dead / warm / opted-out) also flow through
 * the disposition logger. This control owns the deal-progression ladder plus
 * two off-ramps — dead and do_not_contact (the latter has no disposition) —
 * and an optional "Why?" reason applied to every move (falls back to an
 * auto-reason) so lead quality is tracked in lead_state_history.
 *
 * Off-ladder states (dead, do_not_contact, opted_out, responded_unsure,
 * revived_inquiry, consented) render as a "currently" pill rather than a
 * ladder position; the operator can click any ladder stage to move back in.
 * opted_out is a compliance terminal — the backend rejects transitions out of
 * it, so this control shows a notice instead of a clickable track.
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { STATE_LABELS, STATE_CLASS } from './leadsConstants';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// The linear deal ladder, in pipeline order.
const LADDER = [
  'new', 'contacted', 'responded_warm',
  'appointment_set', 'offered', 'under_contract', 'closed',
];

export default function DealStageStepper({ leadId, lead, onChanged }) {
  const { authFetch } = useAuth();
  const [busy, setBusy]   = useState(null); // toState currently being applied
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [why, setWhy]     = useState('');   // optional reason for the next move

  const current = lead?.state;

  // Every move carries a reason (operator transitions require a non-empty one).
  // The "Why?" box wins when filled — it's the lead-quality signal and lands in
  // lead_state_history / the History timeline; otherwise a generic auto-reason
  // is used so a one-click stage advance still works.
  const applyState = async (toState, defaultReason) => {
    if (!toState || busy) return;
    const reason = why.trim() || defaultReason;
    setBusy(toState);
    setError(null);
    setToast(null);
    try {
      const r = await authFetch(`${API_BASE}/api/leads/${leadId}/state`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toState, reason, triggeredBy: 'operator' }),
      });
      const json = await r.json();
      if (!r.ok || !json.success) throw new Error(json.error || `HTTP ${r.status}`);
      setToast(`Moved to ${STATE_LABELS[toState] || toState}.`);
      setWhy('');
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Could not change stage');
    } finally {
      setBusy(null);
    }
  };

  // Compliance terminal — no transitions out (backend rejects fromState==='opted_out').
  if (current === 'opted_out') {
    return (
      <section className="lead-detail-section deal-stepper">
        <h3 className="lead-detail-h">Deal stage</h3>
        <div className="lead-detail-note">
          Opted out is a compliance terminal. Reviving requires verifying legal
          basis, removing the phone from opt_outs directly, and creating a fresh
          lead — not editable here.
        </div>
      </section>
    );
  }

  const onLadder   = LADDER.includes(current);
  const currentIdx = LADDER.indexOf(current);

  return (
    <section className="lead-detail-section deal-stepper">
      <h3 className="lead-detail-h">Deal stage</h3>

      {!onLadder && current && (
        <div className="deal-stepper-current">
          Currently{' '}
          <span className={`leads-state-pill small ${STATE_CLASS[current] || ''}`}>
            {STATE_LABELS[current] || current}
          </span>
          {' '}— pick a stage to move it into the pipeline.
        </div>
      )}

      <div className="deal-stepper-track" role="group" aria-label="Deal stage">
        {LADDER.map((s, i) => {
          const isCurrent = s === current;
          const isPast    = onLadder && i < currentIdx;
          const cls = isCurrent ? 'is-current' : isPast ? 'is-past' : 'is-future';
          return (
            <button
              key={s}
              type="button"
              className={`deal-stepper-node ${cls}`}
              disabled={isCurrent || !!busy}
              onClick={() => applyState(s, `Operator set stage → ${STATE_LABELS[s] || s}`)}
              title={isCurrent ? 'Current stage' : `Move to ${STATE_LABELS[s] || s}`}
            >
              <span className="deal-stepper-dot" />
              <span className="deal-stepper-label">{STATE_LABELS[s] || s}</span>
              {busy === s && <span className="deal-stepper-spin">…</span>}
            </button>
          );
        })}
      </div>

      <div className="deal-stepper-footer">
        <input
          type="text"
          className="deal-stepper-why"
          placeholder="Why? (optional — logged to this lead's history: not motivated, bad number, already sold, low equity, wrong price…)"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          disabled={!!busy}
        />
        <div className="deal-stepper-actions">
          <button
            type="button"
            className="deal-stepper-dead"
            disabled={current === 'dead' || !!busy}
            onClick={() => applyState('dead', 'Operator marked dead')}
          >
            Mark dead
          </button>
          <button
            type="button"
            className="deal-stepper-dnc"
            disabled={current === 'do_not_contact' || !!busy}
            onClick={() => {
              if (window.confirm(
                'Mark this lead do-not-contact? It will be suppressed from the call ' +
                'queue and all outreach. (This is separate from a formal opt-out.)'
              )) {
                applyState('do_not_contact', 'Operator marked do-not-contact');
              }
            }}
          >
            Mark do-not-contact
          </button>
          {error && <span className="deal-stepper-error">{error}</span>}
          {toast && <span className="deal-stepper-toast">{toast}</span>}
        </div>
      </div>
    </section>
  );
}
